'use client';

import { createColumnHelper } from '@tanstack/react-table';

import { Checkbox, Tooltip, TooltipContent, TooltipTrigger } from '../../components';

import type { ColumnDef } from '@tanstack/react-table';

const SELECTION_COLUMN_WIDTH = 40;

export interface CreateSelectionColumnOptions<TData> {
  /**
   * Reason a row can't be selected, shown as a tooltip on its disabled checkbox.
   * The table's `enableRowSelection` should be derived from the same source so
   * "has a reason" and "is disabled" stay in sync.
   */
  getDisabledReason?: (row: TData) => string | undefined;
}

/**
 * Factory for the leading checkbox column used by the shared row-selection
 * feature. The header renders a select-all (indeterminate when partial)
 * checkbox and each cell renders a per-row checkbox.
 *
 * `meta.isSelectionColumn` tells the table body renderers to skip the row-level
 * `<Link>` wrap for this cell so toggling a checkbox never triggers row
 * navigation. Clicks also stop propagation so `onRowClick` rows aren't opened.
 */
export function createSelectionColumn<TData>(
  options: CreateSelectionColumnOptions<TData> = {}
): ColumnDef<TData, unknown> {
  const { getDisabledReason } = options;
  const columnHelper = createColumnHelper<TData>();

  return columnHelper.display({
    id: 'select',
    size: SELECTION_COLUMN_WIDTH,
    enableSorting: false,
    meta: { isSelectionColumn: true, style: { width: SELECTION_COLUMN_WIDTH } },
    // The `*PageRows*` APIs read the row model rather than the core rows, which under
    // the shared tables' `manualPagination` means exactly the rows on screen. So
    // select-all covers what the user can see even when a client-side search has
    // narrowed the list, instead of quietly selecting the rows it filtered out.
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows"
        checked={table.getIsAllPageRowsSelected()}
        // Disabled when no row can actually be selected: the Suspense/loading header
        // (dummy row with selection off), an empty table, or a table where every row
        // has a disabled reason. Guards against toggling a phantom/non-selectable row.
        disabled={!table.getRowModel().rows.some((row) => row.getCanSelect())}
        indeterminate={table.getIsSomePageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
    cell: ({ row }) => {
      const disabled = !row.getCanSelect();
      const checkbox = (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          disabled={disabled}
          onCheckedChange={(value) => row.toggleSelected(value === true)}
          onClick={(e) => e.stopPropagation()}
        />
      );

      if (!disabled) return checkbox;

      // Dim + not-allowed cursor so a non-selectable row reads as disabled at a
      // glance; the tooltip explains why. The span also gives the tooltip a
      // hoverable target and a ref target (Checkbox doesn't forward refs).
      const trigger = (
        <span className="inline-flex cursor-not-allowed opacity-50" onClick={(e) => e.stopPropagation()}>
          {checkbox}
        </span>
      );

      const reason = getDisabledReason?.(row.original);
      if (!reason) return trigger;

      return (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          {/* Right, not top: the checkbox is the leftmost column, so a top tooltip sits over the rows
              above and gets in the way when moving between rows. (Left has no room next to the sidebar.) */}
          <TooltipContent side="right">{reason}</TooltipContent>
        </Tooltip>
      );
    },
  });
}
